import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Edit2, Trash2, SplitSquareHorizontal, Scissors, GitBranch, GitMerge, ChevronUp, ChevronDown, CircleOff, HelpCircle, Repeat2, Circle, Palette, LayoutTemplate, BookmarkPlus } from "lucide-react";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger, ContextMenuTrigger } from "../../base/context-menu/context-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../base/tooltip/tooltip";
import { useReactFlow } from "@xyflow/react";
import { setContextMenuActiveTarget, useContextMenuActiveTarget } from "../flow-kit";
import { classifyEdgeEnds } from "./core/edgeClassifier";
import { classifyCanvasNode, isEventNodeKind } from "./core/nodeKind";
import { useEditorSelectedNodeIds } from "./editor/editorContexts";
import { SwimlaneColorSubmenuContent } from "./SwimlaneColorSubmenu";
const ALL_CAPABILITIES = {
    insertBetween: true,
    forkFromSource: true,
    mergeIntoTarget: true,
    markUnclear: true,
    disconnect: true
};
const ALL_SWIMLANE_CAPABILITIES = {
    moveUp: true,
    moveDown: true
};
/**
 * The "Merge into…" entry. Enabled merges render as a plain item; a gated node
 * renders greyed-out with a hover tooltip explaining why. A disabled
 * `ContextMenuItem` is `pointer-events-none`, so the hover that opens the
 * tooltip lands on the wrapping `<span>`; and since Radix drops disabled items
 * from keyboard roving focus the tooltip is mouse-only, so the reason also
 * rides on the item's `aria-label` for assistive tech. Self-contained
 * `TooltipProvider` keeps it working outside the app shell (e.g. Storybook).
 */ function MergeMenuItem({ nodeId, nodeKind, onMergeNode, mergeNodeDisabledReason }) {
    const { t } = useTranslation();
    const reason = mergeNodeDisabledReason?.(nodeId, nodeKind) ?? null;
    const label = t("processFlow.contextMenu.mergeNode");
    const item = /*#__PURE__*/ _jsxs(ContextMenuItem, {
        disabled: !!reason,
        onClick: ()=>onMergeNode(nodeId, nodeKind),
        "aria-label": reason ? `${label} — ${reason}` : undefined,
        children: [
            /*#__PURE__*/ _jsx(GitMerge, {
                className: "mr-2 h-4 w-4"
            }),
            label
        ]
    });
    if (!reason) return item;
    return /*#__PURE__*/ _jsx(TooltipProvider, {
        children: /*#__PURE__*/ _jsxs(Tooltip, {
            children: [
                /*#__PURE__*/ _jsx(TooltipTrigger, {
                    asChild: true,
                    children: /*#__PURE__*/ _jsx("span", {
                        className: "flex",
                        children: item
                    })
                }),
                /*#__PURE__*/ _jsx(TooltipContent, {
                    children: reason
                })
            ]
        })
    });
}
// Node kinds a step template can hold. Assignments, notes, end nodes and the
// synthetic gateway are not snapshottable.
const TEMPLATABLE_NODE_KINDS = new Set([
    "step",
    "decision",
    "event-start",
    "event-intermediate"
]);
/**
 * The template-able part of the current selection, or the right-clicked node
 * alone when it sits outside the selection — `[]` when it isn't template-able.
 */ function templateSelection(nodes, selectedNodeIds, nodeId) {
    const templatable = nodes.filter((node)=>TEMPLATABLE_NODE_KINDS.has(classifyCanvasNode(node)));
    const inSelection = templatable.filter((node)=>selectedNodeIds.has(node.id));
    if (inSelection.some((node)=>node.id === nodeId)) return inSelection.map((node)=>node.id);
    return templatable.some((node)=>node.id === nodeId) ? [
        nodeId
    ] : [];
}
export function ProcessFlowContextMenu({ targetRef, classifyContext, onAddStep, onAddEvent, canAddEventForStep, onEditNode, onSaveAsStepTemplate, onInsertStepTemplate, onDeleteNode, canDeleteNode, onMarkAsEnd, canMarkAsEnd, onConvertToAssignment, canConvertToAssignment, onConvertToStep, canConvertToStep, onMergeNode, canMergeNode, mergeNodeDisabledReason, onEdgeAction, getEdgeActionCapabilities, onMoveSwimlane, getSwimlaneActionCapabilities, onSetSwimlaneColor, getSwimlaneColor, onSetHandleTerminal, canSetHandleTerminal, children }) {
    const { t } = useTranslation();
    const target = useContextMenuActiveTarget();
    const selectedNodeIds = useEditorSelectedNodeIds();
    const { getNodes } = useReactFlow();
    // Bumped on every open so ContextMenuContent remounts and Radix's Popper
    // re-reads the trigger's virtual anchor. Without this, a second right-click
    // while the menu is open leaves the menu at the original cursor position.
    const [contentKey, setContentKey] = useState(0);
    const edge = useMemo(()=>target?.kind === "edge" ? classifyEdgeEnds(target.source, target.target, target.sourceHandle, classifyContext) : null, [
        target,
        classifyContext
    ]);
    const capabilities = useMemo(()=>edge ? getEdgeActionCapabilities?.(edge) ?? ALL_CAPABILITIES : ALL_CAPABILITIES, [
        edge,
        getEdgeActionCapabilities
    ]);
    // The lane key for swimlane reorder items. Set when right-clicking the
    // label strip (kind: 'swimlane') OR when the right-click hit empty lane
    // body and was tagged on the pane target.
    const activeLaneKey = target?.kind === "swimlane" ? target.laneKey : target?.kind === "pane" ? target.laneKey ?? null : null;
    const swimlaneCapabilities = useMemo(()=>activeLaneKey ? getSwimlaneActionCapabilities?.(activeLaneKey) ?? ALL_SWIMLANE_CAPABILITIES : ALL_SWIMLANE_CAPABILITIES, [
        activeLaneKey,
        getSwimlaneActionCapabilities
    ]);
    const stepTemplateNodeIds = onSaveAsStepTemplate && target?.kind === "node" ? templateSelection(getNodes(), selectedNodeIds, target.nodeId) : [];
    const currentLaneColor = useMemo(()=>activeLaneKey ? getSwimlaneColor?.(activeLaneKey) ?? null : null, [
        activeLaneKey,
        getSwimlaneColor
    ]);
    const swimlaneMenuItems = activeLaneKey && (onMoveSwimlane || onSetSwimlaneColor) ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            onMoveSwimlane && swimlaneCapabilities.moveUp && /*#__PURE__*/ _jsxs(ContextMenuItem, {
                onClick: ()=>onMoveSwimlane(activeLaneKey, "up"),
                children: [
                    /*#__PURE__*/ _jsx(ChevronUp, {
                        className: "mr-2 h-4 w-4"
                    }),
                    t("processFlowEditor.swimlane.moveUp")
                ]
            }),
            onMoveSwimlane && swimlaneCapabilities.moveDown && /*#__PURE__*/ _jsxs(ContextMenuItem, {
                onClick: ()=>onMoveSwimlane(activeLaneKey, "down"),
                children: [
                    /*#__PURE__*/ _jsx(ChevronDown, {
                        className: "mr-2 h-4 w-4"
                    }),
                    t("processFlowEditor.swimlane.moveDown")
                ]
            }),
            onSetSwimlaneColor && /*#__PURE__*/ _jsxs(ContextMenuSub, {
                children: [
                    /*#__PURE__*/ _jsxs(ContextMenuSubTrigger, {
                        children: [
                            /*#__PURE__*/ _jsx(Palette, {
                                className: "mr-2 h-4 w-4"
                            }),
                            t("processFlowEditor.swimlane.color")
                        ]
                    }),
                    /*#__PURE__*/ _jsx(ContextMenuSubContent, {
                        className: "w-56",
                        children: /*#__PURE__*/ _jsx(SwimlaneColorSubmenuContent, {
                            laneKey: activeLaneKey,
                            currentColor: currentLaneColor,
                            onSetSwimlaneColor: onSetSwimlaneColor
                        })
                    })
                ]
            })
        ]
    }) : null;
    return /*#__PURE__*/ _jsxs(ContextMenu, {
        onOpenChange: (open)=>{
            if (open) {
                setContextMenuActiveTarget(targetRef.current);
                setContentKey((k)=>k + 1);
            } else {
                setContextMenuActiveTarget(null);
            }
        },
        children: [
            /*#__PURE__*/ _jsx(ContextMenuTrigger, {
                asChild: true,
                children: /*#__PURE__*/ _jsx("div", {
                    className: "h-full w-full",
                    // Invariant: a descendant handler (pane/node/edge) must set targetRef
                    // during each right-click's bubble phase, or the menu is suppressed —
                    // this is how we avoid Radix opening with a stale target.
                    onContextMenuCapture: ()=>{
                        targetRef.current = null;
                    },
                    onContextMenu: (event)=>{
                        if (targetRef.current === null) event.preventDefault();
                    },
                    children: children
                })
            }),
            /*#__PURE__*/ _jsxs(ContextMenuContent, {
                // The trigger is the non-focusable canvas div, so Radix's focus
                // restore has nothing to restore and only steals focus from whatever
                // a menu item just opened.
                onCloseAutoFocus: (e)=>e.preventDefault(),
                children: [
                    target?.kind === "pane" && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            onAddStep && /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                onClick: ()=>onAddStep(target.position, target.laneKey),
                                children: [
                                    /*#__PURE__*/ _jsx(Plus, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    t("processFlow.contextMenu.addStep")
                                ]
                            }),
                            onAddEvent && /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                onClick: ()=>onAddEvent(target.position),
                                children: [
                                    /*#__PURE__*/ _jsx(Circle, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    t("processFlow.contextMenu.addEvent")
                                ]
                            }),
                            onInsertStepTemplate && /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                onClick: ()=>onInsertStepTemplate(target.position, target.laneKey),
                                children: [
                                    /*#__PURE__*/ _jsx(LayoutTemplate, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    t("processFlow.contextMenu.insertStepTemplate")
                                ]
                            }),
                            swimlaneMenuItems && (onAddStep || onAddEvent || onInsertStepTemplate) && /*#__PURE__*/ _jsx(ContextMenuSeparator, {}),
                            swimlaneMenuItems
                        ]
                    }),
                    target?.kind === "node" && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            onEditNode && /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                onClick: ()=>onEditNode(target.nodeId),
                                children: [
                                    /*#__PURE__*/ _jsx(Edit2, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    isEventNodeKind(target.nodeKind) ? t("processFlow.contextMenu.editEvent") : t("processFlow.contextMenu.editStep")
                                ]
                            }),
                            onSaveAsStepTemplate && stepTemplateNodeIds.length > 0 && /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                onClick: ()=>onSaveAsStepTemplate(stepTemplateNodeIds),
                                children: [
                                    /*#__PURE__*/ _jsx(BookmarkPlus, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    t("processFlow.contextMenu.saveAsStepTemplate", {
                                        count: stepTemplateNodeIds.length
                                    })
                                ]
                            }),
                            onAddEvent && (canAddEventForStep?.(target.nodeId, target.nodeKind) ?? true) && /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                onClick: ()=>onAddEvent({
                                        x: 0,
                                        y: 0
                                    }, target.nodeId),
                                children: [
                                    /*#__PURE__*/ _jsx(Circle, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    t("processFlow.contextMenu.addEventForStep")
                                ]
                            }),
                            onMarkAsEnd && canMarkAsEnd?.(target.nodeId, target.nodeKind) && /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                onClick: ()=>onMarkAsEnd(target.nodeId),
                                children: [
                                    /*#__PURE__*/ _jsx(CircleOff, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    t("processFlow.contextMenu.markAsEnd")
                                ]
                            }),
                            onConvertToAssignment && canConvertToAssignment?.(target.nodeId) && /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                onClick: ()=>onConvertToAssignment(target.nodeId),
                                children: [
                                    /*#__PURE__*/ _jsx(Repeat2, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    t("processFlow.contextMenu.convertToAssignment")
                                ]
                            }),
                            onConvertToStep && canConvertToStep?.(target.nodeId) && /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                onClick: ()=>onConvertToStep(target.nodeId),
                                children: [
                                    /*#__PURE__*/ _jsx(Repeat2, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    t("processFlow.contextMenu.convertToStep")
                                ]
                            }),
                            onMergeNode && canMergeNode?.(target.nodeId, target.nodeKind) && /*#__PURE__*/ _jsx(MergeMenuItem, {
                                nodeId: target.nodeId,
                                nodeKind: target.nodeKind,
                                onMergeNode: onMergeNode,
                                mergeNodeDisabledReason: mergeNodeDisabledReason
                            }),
                            onDeleteNode && (canDeleteNode?.(target.nodeId, target.nodeKind) ?? true) && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    onEditNode && /*#__PURE__*/ _jsx(ContextMenuSeparator, {}),
                                    /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                        onClick: ()=>onDeleteNode(target.nodeId, target.nodeKind),
                                        className: "text-destructive focus:text-destructive",
                                        children: [
                                            /*#__PURE__*/ _jsx(Trash2, {
                                                className: "mr-2 h-4 w-4"
                                            }),
                                            isEventNodeKind(target.nodeKind) ? t("processFlow.contextMenu.deleteEvent") : t("processFlow.contextMenu.deleteStep")
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    target?.kind === "handle" && onSetHandleTerminal && canSetHandleTerminal?.(target.nodeId, target.handleId) && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                onClick: ()=>onSetHandleTerminal(target.nodeId, target.handleId, "NONE"),
                                children: [
                                    /*#__PURE__*/ _jsx(CircleOff, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    t("processFlow.contextMenu.markAsEnd")
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                onClick: ()=>onSetHandleTerminal(target.nodeId, target.handleId, "UNCLEAR"),
                                children: [
                                    /*#__PURE__*/ _jsx(HelpCircle, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    t("processFlow.contextMenu.markUnclear")
                                ]
                            })
                        ]
                    }),
                    target?.kind === "swimlane" && swimlaneMenuItems,
                    edge && onEdgeAction && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            capabilities.insertBetween && /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                onClick: ()=>onEdgeAction("insertBetween", edge),
                                children: [
                                    /*#__PURE__*/ _jsx(SplitSquareHorizontal, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    t("processFlow.contextMenu.insertBetween")
                                ]
                            }),
                            capabilities.forkFromSource && /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                onClick: ()=>onEdgeAction("forkFromSource", edge),
                                children: [
                                    /*#__PURE__*/ _jsx(GitBranch, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    t("processFlow.contextMenu.forkFromSource")
                                ]
                            }),
                            capabilities.mergeIntoTarget && /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                onClick: ()=>onEdgeAction("mergeIntoTarget", edge),
                                children: [
                                    /*#__PURE__*/ _jsx(GitMerge, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    t("processFlow.contextMenu.mergeIntoTarget")
                                ]
                            }),
                            capabilities.markUnclear && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    (capabilities.insertBetween || capabilities.forkFromSource || capabilities.mergeIntoTarget) && /*#__PURE__*/ _jsx(ContextMenuSeparator, {}),
                                    /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                        onClick: ()=>onEdgeAction("markUnclear", edge),
                                        children: [
                                            /*#__PURE__*/ _jsx(HelpCircle, {
                                                className: "mr-2 h-4 w-4"
                                            }),
                                            t("processFlow.contextMenu.markUnclear")
                                        ]
                                    })
                                ]
                            }),
                            capabilities.disconnect && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    (capabilities.insertBetween || capabilities.forkFromSource || capabilities.mergeIntoTarget || capabilities.markUnclear) && /*#__PURE__*/ _jsx(ContextMenuSeparator, {}),
                                    /*#__PURE__*/ _jsxs(ContextMenuItem, {
                                        onClick: ()=>onEdgeAction("disconnect", edge),
                                        className: "text-destructive focus:text-destructive",
                                        children: [
                                            /*#__PURE__*/ _jsx(Scissors, {
                                                className: "mr-2 h-4 w-4"
                                            }),
                                            t("processFlow.contextMenu.deleteEdge")
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }, contentKey)
        ]
    });
}

//# sourceMappingURL=ProcessFlowContextMenu.js.map